import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import { apiService } from '../services/api'
import type { RegisterField } from '../types/api'

interface FormsContextValue {
  registerFields: RegisterField[]
  terms: string | null
  isLoading: boolean
  isTermsLoading: boolean
  isFieldsLoading: boolean
  loadRegisterFields: () => Promise<void>
  loadTerms: () => Promise<void>
}

const FormsContext = createContext<FormsContextValue | undefined>(undefined)

interface FormsProviderProps {
  children: ReactNode
}

export const FormsProvider = ({ children }: FormsProviderProps) => {
  const [registerFields, setRegisterFields] = useState<RegisterField[]>([])
  const [terms, setTerms] = useState<string | null>(null)
  const [isFieldsLoading, setIsFieldsLoading] = useState(true)
  const [isTermsLoading, setIsTermsLoading] = useState(true)

  const loadRegisterFields = useCallback(async () => {
    if (!isFieldsLoading || registerFields.length > 0) return

    try {
      const response = await apiService.getRegisterFields()
      if (!response.profile_fields) {
        setIsFieldsLoading(false)
        return
      }

      setRegisterFields(response.profile_fields)
    } catch (error) {
      console.error('Failed to load register fields:', error)
    } finally {
      setIsFieldsLoading(false)
    }
  }, [isFieldsLoading, registerFields.length])

  const loadTerms = useCallback(async () => {
    if (!isTermsLoading || terms) return

    try {
      const response = await apiService.getTerms()
      if (!response.terms) {
        setIsTermsLoading(false)
        return
      }

      let termsHtml = response.terms

      const markedModule = await import('marked')
      termsHtml = markedModule.parse(termsHtml) as string

      const DOMPurifyModule = await import('dompurify')
      const DOMPurify = DOMPurifyModule.default || DOMPurifyModule

      if (DOMPurify && typeof DOMPurify.sanitize === 'function')
        termsHtml = DOMPurify.sanitize(termsHtml) || ''

      setTerms(termsHtml)
    } catch (error) {
      console.error('Failed to load terms:', error)
    } finally {
      setIsTermsLoading(false)
    }
  }, [isTermsLoading, terms])

  useEffect(() => {
    loadRegisterFields()
    loadTerms()
  }, [])

  const value: FormsContextValue = useMemo(
    () => ({
      registerFields,
      terms,
      isLoading: isFieldsLoading || isTermsLoading,
      isTermsLoading,
      isFieldsLoading,
      loadRegisterFields,
      loadTerms,
    }),
    [
      registerFields,
      terms,
      isFieldsLoading,
      isTermsLoading,
      loadRegisterFields,
      loadTerms,
    ]
  )

  return <FormsContext.Provider value={value}>{children}</FormsContext.Provider>
}

export const useForms = (): FormsContextValue => {
  const context = useContext(FormsContext)
  if (!context) throw new Error('useForms must be used within FormsProvider')
  return context
}
